"use client";

import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { handleResponse, useMutationHandler } from "./utils";

export const kApiWatchSWRKeys = {
  watch: (resourceType: string, resourceId: string) =>
    `/api/watches?resourceType=${encodeURIComponent(resourceType)}&resourceId=${encodeURIComponent(resourceId)}`,
  watches: "/api/watches",
};

export const kApiNotificationSWRKeys = {
  prefs: "/api/notifications/prefs",
  list: "/api/notifications",
};

type WatchRow = {
  id: string;
  userId: string;
  resourceType: string;
  resourceId: string;
  events: string[];
  fieldFilters: string[] | null;
};

async function getWatch(url: string) {
  const res = await fetch(url);
  return handleResponse<{
    watch: WatchRow | null;
    workspaceWatch?: WatchRow | null;
    workspaceId?: string | null;
  }>(res);
}

export function useWatch(params: {
  resourceType: string;
  resourceId: string;
}) {
  return useSWR(
    kApiWatchSWRKeys.watch(params.resourceType, params.resourceId),
    getWatch
  );
}

async function upsertWatch(
  url: string,
  opts: {
    arg: {
      resourceType: string;
      resourceId: string;
      events: string[];
      fieldFilters?: string[] | null;
    };
  }
) {
  const res = await fetch(url, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(opts.arg),
  });
  return handleResponse<WatchRow>(res);
}

async function deleteWatch(
  url: string,
  opts: {
    arg: { resourceType: string; resourceId: string };
  }
) {
  const res = await fetch(url, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(opts.arg),
  });
  return handleResponse<{ ok: boolean }>(res);
}

export function useUpsertWatch(opts: {
  resourceType: string;
  resourceId: string;
}) {
  const mutationHandler = useMutationHandler(upsertWatch, {
    invalidate: [kApiWatchSWRKeys.watch(opts.resourceType, opts.resourceId)],
  });
  return useSWRMutation(kApiWatchSWRKeys.watches, mutationHandler);
}

export function useDeleteWatch(opts: {
  resourceType: string;
  resourceId: string;
}) {
  const mutationHandler = useMutationHandler(deleteWatch, {
    invalidate: [kApiWatchSWRKeys.watch(opts.resourceType, opts.resourceId)],
  });
  return useSWRMutation(kApiWatchSWRKeys.watches, mutationHandler);
}

type Prefs = {
  userId: string;
  digestIntervalHours: number;
  timezone: string;
  pausedUntil: string | Date | null;
  dueSoonHours: number;
  emailDigest: boolean;
  inApp: boolean;
  push: boolean;
};

async function getPrefs(url: string) {
  const res = await fetch(url);
  return handleResponse<{ prefs: Prefs }>(res);
}

export function useNotificationPrefs() {
  return useSWR(kApiNotificationSWRKeys.prefs, getPrefs);
}

async function updatePrefs(
  url: string,
  opts: { arg: Record<string, unknown> }
) {
  const res = await fetch(url, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(opts.arg),
  });
  return handleResponse<{ prefs: Prefs }>(res);
}

export function useUpdateNotificationPrefs() {
  const mutationHandler = useMutationHandler(updatePrefs, {
    invalidate: [kApiNotificationSWRKeys.prefs],
  });
  return useSWRMutation(kApiNotificationSWRKeys.prefs, mutationHandler);
}

type InAppNotification = {
  id: string;
  title: string;
  body: string;
  href: string;
  readAt: string | Date | null;
  createdAt: string | Date;
};

async function listNotifications(url: string) {
  const res = await fetch(url);
  return handleResponse<{
    notifications: InAppNotification[];
    unreadCount: number;
    nextCursor: string | null;
  }>(res);
}

export function useInAppNotifications() {
  return useSWR(kApiNotificationSWRKeys.list, listNotifications, {
    refreshInterval: 30_000,
  });
}

async function markRead(
  url: string,
  opts: { arg: { ids?: string[]; all?: boolean } }
) {
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(opts.arg),
  });
  return handleResponse<{ ok: boolean }>(res);
}

export function useMarkNotificationsRead() {
  const mutationHandler = useMutationHandler(markRead, {
    invalidate: [kApiNotificationSWRKeys.list],
    showToast: false,
  });
  return useSWRMutation(kApiNotificationSWRKeys.list, mutationHandler);
}
